/** OTD band helpers for customer list quick filters and card colours. */

export type OtdBand = 'excellent' | 'good' | 'fair' | 'poor';

export interface OtdThresholds {
  excellent: number;
  good: number;
  poor: number;
}

export const DEFAULT_OTD_THRESHOLDS: OtdThresholds = {
  excellent: 90,
  good: 70,
  poor: 50,
};

export interface OtdQuickFilterChip {
  value: OtdBand | '';
  label: string;
  activeClassName: string;
  idleClassName: string;
}

/**
 * Parse OTD threshold settings from performance settings API.
 */
export function parseOtdThresholds(settings?: {
  otd_excellent_threshold?: string;
  otd_good_threshold?: string;
  otd_poor_threshold?: string;
} | null): OtdThresholds {
  return {
    excellent: parseFloat(settings?.otd_excellent_threshold ?? '') || DEFAULT_OTD_THRESHOLDS.excellent,
    good: parseFloat(settings?.otd_good_threshold ?? '') || DEFAULT_OTD_THRESHOLDS.good,
    poor: parseFloat(settings?.otd_poor_threshold ?? '') || DEFAULT_OTD_THRESHOLDS.poor,
  };
}

/**
 * Classify an OTD percentage into a threshold band.
 */
export function classifyOtdBand(rate: number | null | undefined, thresholds: OtdThresholds): OtdBand {
  const value = rate ?? 0;
  if (value >= thresholds.excellent) return 'excellent';
  if (value >= thresholds.good) return 'good';
  if (thresholds.poor < thresholds.good && value >= thresholds.poor) return 'fair';
  return 'poor';
}

/**
 * Build quick-filter chip definitions from configured thresholds.
 */
export function buildOtdQuickFilterChips(thresholds: OtdThresholds): OtdQuickFilterChip[] {
  const chips: OtdQuickFilterChip[] = [
    {
      value: 'excellent',
      label: `Excellent ≥${thresholds.excellent}%`,
      activeClassName: 'bg-green-600 text-white border-green-600',
      idleClassName: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    },
    {
      value: 'good',
      label: `Good ${thresholds.good}–${Math.max(thresholds.excellent - 0.01, thresholds.good).toFixed(0)}%`,
      activeClassName: 'bg-amber-500 text-white border-amber-500',
      idleClassName: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
    },
  ];

  if (thresholds.poor < thresholds.good) {
    chips.push({
      value: 'fair',
      label: `Fair ${thresholds.poor}–${Math.max(thresholds.good - 0.01, thresholds.poor).toFixed(0)}%`,
      activeClassName: 'bg-orange-500 text-white border-orange-500',
      idleClassName: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
    });
  }

  chips.push({
    value: 'poor',
    label: `Poor <${thresholds.good}%`,
    activeClassName: 'bg-red-600 text-white border-red-600',
    idleClassName: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
  });

  return chips;
}

/** Tailwind text colour for OTD % on customer cards. */
export function getOtdColorClass(rate: number, thresholds: OtdThresholds): string {
  switch (classifyOtdBand(rate, thresholds)) {
    case 'excellent':
      return 'text-green-600';
    case 'good':
      return 'text-amber-600';
    case 'fair':
      return 'text-orange-600';
    default:
      return 'text-red-600';
  }
}
